import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateAIMinutesTemplateDtoSchema,
  PatchAIMinutesTemplateDtoSchema,
  type AIMinutesTemplate,
} from "@repo/types";
import { cacheDefaults } from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type AIMinutesTemplateGetKey = {
  type: "get";
  tenantId: string;
  aiMinutesTemplateId: string;
};
type AIMinutesTemplateListKey = { type: "list"; tenantId: string };
type AIMinutesTemplateKey = AIMinutesTemplateGetKey | AIMinutesTemplateListKey;

export type AIMinutesTemplateDto = AIMinutesTemplate & {
  tenantId: string;
};

export type AIMinutesTemplateIdPath = {
  tenantId: string;
  aiMinutesTemplateId: string | null;
};

export class AIMinutesTemplateRepository {
  private cache: RepoCache<
    string,
    AIMinutesTemplateDto,
    ObjectKeyHasher,
    AnyClock
  >;
  private hooks = new RepoMutationHooks<
    AIMinutesTemplateIdPath,
    AIMinutesTemplateDto
  >();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(aiMinutesTemplate: AIMinutesTemplateDto) {
    this.cache.publish(aiMinutesTemplate.id, aiMinutesTemplate);
  }

  aiMinutesTemplateToDto(
    tenantId: string,
    aiMinutesTemplate: AIMinutesTemplate
  ): AIMinutesTemplateDto;
  aiMinutesTemplateToDto(tenantId: string, aiMinutesTemplate?: null): null;
  aiMinutesTemplateToDto(
    tenantId: string,
    aiMinutesTemplate?: AIMinutesTemplate | null
  ): AIMinutesTemplateDto | null;
  aiMinutesTemplateToDto(
    tenantId: string,
    aiMinutesTemplate?: AIMinutesTemplate | null
  ): AIMinutesTemplateDto | null {
    return aiMinutesTemplate ? { ...aiMinutesTemplate, tenantId } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as AIMinutesTemplateKey;

        switch (key.type) {
          case "get": {
            const { tenantId, aiMinutesTemplateId } = key;
            const aiMinutesTemplate = (
              await apiService.v1.getAIMinutesTemplate(
                tenantId,
                aiMinutesTemplateId
              )
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(aiMinutesTemplateId);
            if (!prev || prev.tenantId === tenantId) {
              publish(
                aiMinutesTemplateId,
                this.aiMinutesTemplateToDto(tenantId, aiMinutesTemplate)
              );
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const aiMinutesTemplates = (
              await apiService.v1.listAIMinutesTemplates(tenantId)
            ).data;
            for (const aiMinutesTemplate of aiMinutesTemplates) {
              publish(
                aiMinutesTemplate.id,
                this.aiMinutesTemplateToDto(tenantId, aiMinutesTemplate)
              );
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as AIMinutesTemplateKey;

        switch (key.type) {
          case "get": {
            return table.get(key.aiMinutesTemplateId) ?? null;
          }
          case "list": {
            const aiMinutesTemplates: AIMinutesTemplateDto[] = [];
            for (const aiMinutesTemplate of table.values()) {
              if (aiMinutesTemplate.tenantId === key.tenantId) {
                aiMinutesTemplates.push(aiMinutesTemplate);
              }
            }
            return aiMinutesTemplates;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(
    tenantId: string,
    aiMinutesTemplateId: string
  ): AIMinutesTemplateGetKey {
    return { type: "get", tenantId, aiMinutesTemplateId };
  }
  private listKey(tenantId: string): AIMinutesTemplateListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    aiMinutesTemplateId: string,
    options?: GetObservableOptions
  ): Observable<AIMinutesTemplateDto | null> {
    if (!tenantId || !aiMinutesTemplateId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, aiMinutesTemplateId);
    return this.cache.observable(key, options);
  }
  getCached(
    tenantId: string,
    aiMinutesTemplateId: string
  ): AIMinutesTemplateDto | null {
    const key = this.getKey(tenantId, aiMinutesTemplateId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<AIMinutesTemplateDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): AIMinutesTemplateDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Partial<AIMinutesTemplateDto>
  ): Promise<AIMinutesTemplateDto> {
    if (!tenantId) {
      throw new Error(
        "Failed to create AI minutes template: tenantId is blank."
      );
    }

    const hooks = this.hooks.execute(
      { tenantId, aiMinutesTemplateId: null },
      "create"
    );
    data = await hooks.before(data);

    let created = this.aiMinutesTemplateToDto(
      tenantId,
      (
        await apiService.v1.createAIMinutesTemplate(
          tenantId,
          CreateAIMinutesTemplateDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new AI minutes template
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    aiMinutesTemplateId: string,
    data: Partial<AIMinutesTemplateDto>
  ): Promise<AIMinutesTemplateDto> {
    if (!tenantId || !aiMinutesTemplateId) {
      throw new Error(
        "Failed to update AI minutes template: tenantId or aiMinutesTemplateId is blank."
      );
    }

    const hooks = this.hooks.execute(
      { tenantId, aiMinutesTemplateId },
      "update"
    );
    data = await hooks.before(data);

    let updated = this.aiMinutesTemplateToDto(
      tenantId,
      (
        await apiService.v1.updateAIMinutesTemplate(
          tenantId,
          aiMinutesTemplateId,
          PatchAIMinutesTemplateDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated AI minutes template
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, aiMinutesTemplateId: string) {
    if (!tenantId || !aiMinutesTemplateId) {
      throw new Error(
        "Failed to delete AI minutes template: tenantId or aiMinutesTemplateId is blank."
      );
    }

    const hooks = this.hooks.execute(
      { tenantId, aiMinutesTemplateId },
      "delete"
    );
    await hooks.before(null);

    await apiService.v1.deleteAIMinutesTemplate(tenantId, aiMinutesTemplateId);

    await hooks.after(null);
    this.cache.publish(aiMinutesTemplateId, null);
  }

  invalidate(aiMinutesTemplate: OnlyRequire<AIMinutesTemplateDto, "id">) {
    this.cache.invalidateEntry(aiMinutesTemplate.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<AIMinutesTemplateDto>): AIMinutesTemplateDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      name: "",
      data: "[]",
      ...data,
    });
  }
}

export const aiMinutesTemplateRepo = new AIMinutesTemplateRepository();
